import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { NON_CALLS } from '@rsos/constants/incidentTypes';
import { getMostRecentLoc } from '@rsos/utils/getMostRecentLoc';
import InfoWindowContent from '../../InfoWindow/InfoWindowContent';
import { createWebMercatorViewport } from '../utils';
import { StyledCallPopover, StyledPopoverCaret } from './MapPopover.styles';

const MemoizedNonCallMapPopover = React.memo(({ x, y, data }) => {
  const coord = {
    x: x || 0,
    y: y || 0,
  };

  if (data?.nonCallData?.body) {
    let normalizedY = coord.y;

    return (
      <StyledCallPopover x={coord.x} y={normalizedY}>
        <InfoWindowContent type={NON_CALLS} data={data?.nonCallData} />
        <StyledPopoverCaret isGoogleMap />
      </StyledCallPopover>
    );
  }
  return <></>;
});

const NonCallMapPopover = ({ overlay }) => {
  const [popoverPosition, setPopoverPosition] = useState(null);
  const [popoverData, setPopoverData] = useState(null);
  const basemap = useSelector(state => state.sinatra.ui.basemap);

  const nonCallPopover = useSelector(state => state.mapPopover.nonCallPopover);
  const selectedCallerID = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );
  const callQueue = useSelector(state => state.emergencyCalls.callQueue);
  const selectedCallHasLocation = getMostRecentLoc(callQueue[selectedCallerID]);
  const selectedAirAmbulance = useSelector(
    state => state.airAmbulance.selectedAirAmbulance,
  );
  const nonCallAirAmbulances = useSelector(
    state => state.airAmbulance.requests.nonCalls,
  );
  const hoveredID = useSelector(
    state => state.airAmbulance.hoveredAirAmbulance,
  );
  const nonCallPopoverNormalized = useMemo(() => {
    if (nonCallPopover) {
      return nonCallPopover;
    } else {
      // Only show popover for a non call when in all view or when it's selected
      if (!selectedCallHasLocation) {
        if (selectedAirAmbulance) {
          if (hoveredID === selectedAirAmbulance.airAmbulanceID) {
            return nonCallAirAmbulances[hoveredID];
          }
        } else {
          if (hoveredID) {
            if (nonCallAirAmbulances[hoveredID]) {
              return nonCallAirAmbulances[hoveredID];
            }
          }
        }
      }
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    nonCallAirAmbulances,
    hoveredID,
    nonCallPopover,
    selectedCallHasLocation,
  ]);

  /**
   * Repositions the popover based on the current viewState.
   * @param {Object} viewState
   */
  const reposition = useCallback(
    (viewState, nonCallPopoverNormalized) => {
      const viewport = createWebMercatorViewport(viewState, basemap);
      if (nonCallPopoverNormalized) {
        let nonCallLocPosition = [
          parseFloat(nonCallPopoverNormalized?.body?.location?.longitude),
          parseFloat(nonCallPopoverNormalized?.body?.location?.latitude),
        ];
        const [x, y] = viewport.project(nonCallLocPosition);

        setPopoverData({
          nonCallData: nonCallPopoverNormalized,
          x,
          y,
        });
        setPopoverPosition({ x, y });
      }
    },
    [setPopoverPosition, basemap],
  );

  // Reposition whenever the popover data changes.
  useEffect(() => {
    if (overlay && nonCallPopoverNormalized) {
      const viewState = overlay.getViewState();
      reposition(viewState, nonCallPopoverNormalized);
    }

    if (!nonCallPopoverNormalized) {
      setPopoverPosition(null);
      setPopoverData(null);
    }
  }, [overlay, nonCallPopoverNormalized, reposition]);

  return (
    <>
      {popoverPosition && popoverData && (
        <MemoizedNonCallMapPopover
          x={popoverPosition?.x}
          y={popoverPosition?.y}
          data={popoverData}
        />
      )}
    </>
  );
};

export default NonCallMapPopover;
